import { useCallback, useRef } from 'react'

/**
 * Tracks cursor position across Ant Design TextArea re-renders and modal transitions.
 * Returns helpers to save position on selection, and a ref callback to restore it on mount.
 */
export function useCursorRestore() {
  const cursorPosRef = useRef(0)
  const hasRestoredRef = useRef(false)

  const saveCursor = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      cursorPosRef.current = (e.target as HTMLTextAreaElement).selectionStart
    },
    []
  )

  const createRestoreRef = useCallback(
    (opts?: { guard?: React.MutableRefObject<boolean> }) =>
      (
        el: {
          resizableTextArea?: { textArea?: HTMLTextAreaElement | null }
        } | null
      ) => {
        const ta = el?.resizableTextArea?.textArea
        if (!ta) return
        if (opts?.guard) {
          if (opts.guard.current) return
          opts.guard.current = true
        }
        requestAnimationFrame(() => {
          ta.focus()
          ta.setSelectionRange(cursorPosRef.current, cursorPosRef.current)
        })
      },
    []
  )

  return { cursorPosRef, saveCursor, createRestoreRef, hasRestoredRef }
}
